import {Component, OnDestroy, OnInit} from '@angular/core';
import {User} from '../../../models/user.model';
import {UNPAID_LIMIT_FIELDS, UNPAID_LIMIT_MIN, UNPAID_LIMIT_MAX} from '../../common/vacation/unpaid-leave.constants';
import {unpaidLimitsConfigured, unpaidLimitsValid} from '../../common/vacation/unpaid-leave-policy';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/timer';
import {
  GeneralSetting,
  GeneralSettingEditableField,
  mergeGeneralSetting,
} from '../../../models/general-setting.model';
import { TIMEOUTS } from '../../../modals/const-timeout';
import { AppConstants } from '../view-user-profile/app.constants';
import {
  VacationLocationPolicy,
  VacationPolicy,
  VacationPolicyUpdateRequest
} from '../../common/vacation/vacation.models';
import {
  AI_SETTING_VIEW_FIELDS,
  LEARNING_TEST_SETTING_VIEW_FIELDS,
  SettingsSection,
  VACATION_POLICY_LIMITS,
} from './view-user-report.option';
import { ViewUserReportHelper } from './view-user-report.helper';
import { ViewUserReportGateway } from './view-user-report.gateway';
import { PayrollCurrencySettingsService } from './payroll-currency-settings.service';
import { ViewUserReportUiCoordinator } from './view-user-report-ui.coordinator';

@Component({
  selector: 'app-view-user-report',
  templateUrl: './view-user-report.component.html',
  styleUrls: ['./view-user-report.component.css'],
  providers: [
    ViewUserReportGateway,
    ViewUserReportUiCoordinator,
    PayrollCurrencySettingsService
  ]
})
export class ViewUserReportComponent implements OnInit, OnDestroy {
  readonly unpaidLimitFields = UNPAID_LIMIT_FIELDS;
  readonly unpaidLimitMin = UNPAID_LIMIT_MIN;
  readonly unpaidLimitMax = UNPAID_LIMIT_MAX;
  readonly trackUnpaidField = (_index: number, field: {key: string}): string => field.key;
  public readonly sections = SettingsSection;
  public readonly aiSettings = AI_SETTING_VIEW_FIELDS;
  public readonly learningTestSettings = LEARNING_TEST_SETTING_VIEW_FIELDS;
  public readonly trackBySettingAction = ViewUserReportHelper.trackGeneralSetting;
  public readonly trackByVacationLocationPolicy =
    ViewUserReportHelper.trackVacationLocationPolicy;
  private user: User;
  public isLoaded = false;
  public errorInfo = false;
  visible: SettingsSection = SettingsSection.NONE;
  obj: GeneralSetting = mergeGeneralSetting(null);
  saveSucceeded = false;
  generalSettingsError = false;
  vacationPolicy: VacationPolicy = null;
  vacationPolicyLoading = false;
  vacationPolicySaving = false;
  vacationPolicyError = false;
  private unpaidLimitsRequired = false;

  private successTimer: Subscription;

  constructor(private gateway: ViewUserReportGateway,
              public readonly payrollSettings: PayrollCurrencySettingsService,
              private ui: ViewUserReportUiCoordinator) {}


  ngOnInit(): void {
    this.user = this.gateway.enterForCurrentAdmin();
    if (!this.user) {
      return;
    }
    this.applyInitialSection();
    this.loadCurrentUser();
    this.loadGeneralSettings();
  }

  ngOnDestroy(): void {
    this.clearSuccessTimer();
  }

  private loadCurrentUser(): void {
    this.gateway.loadCurrentUser(this.user.id).subscribe(
      () => this.setLoadState(false),
      () => this.setLoadState(true)
    );
  }

  private setLoadState(hasError: boolean): void {
    this.isLoaded = true;
    this.errorInfo = hasError;
  }


  loadGeneralSettings(): void {
    this.generalSettingsError = false;
    this.gateway.loadGeneralSettings().subscribe(
        (res) => {
          this.obj = mergeGeneralSetting(res.body);
        },
        () => {
          this.generalSettingsError = true;
        }
      );
  }

  openEdit(action: GeneralSettingEditableField, title: string, value: number): void {
    this.ui.openEdit({action: action, title: title, settings: this.obj, value: value}).then((result) => {
      if (result === AppConstants.MODAL_RESULT.OK) {
        this.showSaveSuccess();
        this.loadGeneralSettings();
      }
    });
  }

  selectSection(action: SettingsSection): void {
    this.visible = action;
    if (action === SettingsSection.KARMA) {
      this.payrollSettings.load();
    }
    if (action === SettingsSection.VACATION) {
      this.loadVacationPolicy();
    }
  }

  toggleSection(action: SettingsSection): void {
    this.selectSection(this.visible === action ? SettingsSection.NONE : action);
  }

  saveVacationPolicy(): void {
    if (!this.canSaveVacationPolicy) {
      return;
    }
    this.vacationPolicySaving = true;
    this.vacationPolicyError = false;
    const request: VacationPolicyUpdateRequest = {
      annualPaidLeaveDays: this.vacationPolicy.annualPaidLeaveDays,
      minimumWorkingPercentage: this.vacationPolicy.minimumWorkingPercentage,
      locationPolicies: this.vacationPolicy.locationPolicies
    };
    if (this.unpaidPolicyConfigured) {
      request.unpaidSingleDayLimit = this.vacationPolicy.unpaidSingleDayLimit;
      request.unpaidMultiDayLimit = this.vacationPolicy.unpaidMultiDayLimit;
      request.unpaidMonthlyDayLimit = this.vacationPolicy.unpaidMonthlyDayLimit;
    }
    this.gateway.saveVacationPolicy(request).subscribe(
      policy => this.handleVacationPolicySaved(policy), () => this.handleVacationPolicyFailed()
    );
  }

  get canSaveVacationPolicy(): boolean {
    return !!this.vacationPolicy
      && this.unpaidPolicyValid
      && !this.vacationPolicySaving
      && this.isIntegerBetween(
        this.vacationPolicy.annualPaidLeaveDays,
        VACATION_POLICY_LIMITS.MIN_ANNUAL_DAYS,
        VACATION_POLICY_LIMITS.MAX_ANNUAL_DAYS)
      && this.isIntegerBetween(
        this.vacationPolicy.minimumWorkingPercentage,
        VACATION_POLICY_LIMITS.MIN_WORKING_PERCENTAGE,
        VACATION_POLICY_LIMITS.MAX_WORKING_PERCENTAGE)
      && this.hasValidVacationLocationPolicies(this.vacationPolicy.locationPolicies);
  }

  get unpaidPolicyValid(): boolean {
    return !!this.vacationPolicy && unpaidLimitsValid(this.vacationPolicy) &&
      (!this.unpaidLimitsRequired || this.unpaidPolicyConfigured);
  }

  get unpaidPolicyConfigured(): boolean {
    return !!this.vacationPolicy && unpaidLimitsConfigured(this.vacationPolicy);
  }

  getSettingValue(action: GeneralSettingEditableField): number {
    return this.obj[action];
  }

  public savePayrollCurrencySettings(): void {
    this.payrollSettings.save(() => this.showSaveSuccess());
  }

  private applyInitialSection(): void {
    const initialSection = this.ui.initialSection();
    if (initialSection !== undefined) {
      this.selectSection(initialSection);
    }
  }

  private loadVacationPolicy(): void {
    if (this.vacationPolicy || this.vacationPolicyLoading) {
      return;
    }
    this.vacationPolicyLoading = true;
    this.vacationPolicyError = false;
    this.gateway.loadVacationPolicy().subscribe(
      policy => this.handleVacationPolicyLoaded(policy),
      () => this.handleVacationPolicyFailed()
    );
  }

  private handleVacationPolicyLoaded(policy: VacationPolicy): void {
    if (policy) {
      policy.locationPolicies = policy.locationPolicies || [];
    }
    this.vacationPolicy = policy;
    this.unpaidLimitsRequired = !!policy && unpaidLimitsConfigured(policy);
    this.vacationPolicyLoading = false;
  }

  private handleVacationPolicySaved(policy: VacationPolicy): void {
    this.handleVacationPolicyLoaded(policy);
    this.vacationPolicySaving = false;
    this.showSaveSuccess();
  }

  private handleVacationPolicyFailed(): void {
    this.vacationPolicyLoading = false;
    this.vacationPolicySaving = false;
    this.vacationPolicyError = true;
  }

  private hasValidVacationLocationPolicies(
    policies: VacationLocationPolicy[]
  ): boolean {
    return Array.isArray(policies)
      && policies.every(policy => !!policy
        && !!policy.calendarMarkerId
        && this.isIntegerBetween(
          policy.annualPaidLeaveDays,
          VACATION_POLICY_LIMITS.MIN_ANNUAL_DAYS,
          VACATION_POLICY_LIMITS.MAX_ANNUAL_DAYS));
  }

  private isIntegerBetween(value: number, minimum: number, maximum: number): boolean {
    return ViewUserReportHelper.isNonNegativeFiniteNumber(value)
      && Math.floor(value) === value
      && value >= minimum
      && value <= maximum;
  }

  private showSaveSuccess(): void {
    this.clearSuccessTimer();
    this.saveSucceeded = true;
    this.successTimer = Observable.timer(TIMEOUTS.MEDIUM)
      .subscribe(() => this.saveSucceeded = false);
  }

  private clearSuccessTimer(): void {
    if (this.successTimer) {
      this.successTimer.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-report/view-user-report.component.ts